import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Material } from '@texas/api/endpoints/metadata/materialsApi';
import {
  MaterialComplianceDocument,
  materialComplianceDocumentsApi,
} from '@texas/api/endpoints/metadata/materialComplianceDocumentsApi';
import { complianceDocumentDefinitionsApi } from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ComplianceDocumentDefinitionOption } from '@texas/components/configuration/complianceDocumentDefinitions/ComplianceDocumentDefinitionOption';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { EntityDirectoryEmptyState } from '@texas/components/shared/entityDirectory/EntityDirectoryEmptyState';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { entityWorkspaceStyles } from '@texas/resources/theme/styles/entityWorkspace';
import { materialComplianceDocumentEvents } from './events';

interface Props {
  material: Material;
  documents: MaterialComplianceDocument[];
  onClose: () => void;
}

export function AssignMaterialComplianceDocument({
  material,
  documents,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    data,
    loading: definitionsLoading,
    error: definitionsError,
    refetch,
  } = useApiResource(complianceDocumentDefinitionsApi.getAll);
  const { request, loading, error } = useApiRequest(
    materialComplianceDocumentsApi.assign,
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const assignedIds = new Set(
    documents.map((document) => document.complianceDocumentDefinitionId),
  );
  const query = searchTerm.trim().toLocaleLowerCase();
  const definitions = (data ?? []).filter(
    (definition) =>
      !definition.archived &&
      `${definition.name} ${definition.description}`
        .toLocaleLowerCase()
        .includes(query),
  );
  const pending =
    definitionsLoading || (data === null && definitionsError === null);
  const canAssign =
    !loading &&
    !pending &&
    !definitionsError &&
    selectedId !== null &&
    !assignedIds.has(selectedId) &&
    definitions.some((definition) => definition.id === selectedId);

  useEffect(() => {
    refetch({
      searchTerm: '',
      includeArchived: false,
      sortBy: 'name',
      sortDesc: false,
    });
  }, [refetch]);

  return (
    <Modal
      variant="entityDirectory"
      size="xl"
      isOpen={true}
      onClose={close}
      scrollBehavior="inside"
      closeOnOverlayClick={!loading}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('customerComplianceDocuments.assignFromConfigs')}
        </ModalHeader>
        <ModalCloseButton isDisabled={loading} />
        <ModalBody>
          <Stack spacing={4}>
            <Text variant="entity-directory-description">
              {t('customerComplianceDocuments.choose', { name: material.name })}
            </Text>
            <SearchFilterInput
              style="entityDirectory"
              debounceDelay={0}
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('customerComplianceDocuments.search')}
            />
            <ErrorDetails error={definitionsError} />
            <ErrorDetails error={error} />
            {pending ? (
              <SpinnerWithLabel label={t('general.loadingData')} />
            ) : (
              !definitionsError &&
              (definitions.length === 0 ? (
                <EntityDirectoryEmptyState>
                  {t('customerComplianceDocuments.noDefinitions')}
                </EntityDirectoryEmptyState>
              ) : (
                <Flex sx={entityWorkspaceStyles.branchItems}>
                  {definitions.map((definition) => (
                    <ComplianceDocumentDefinitionOption
                      key={definition.id}
                      definition={definition}
                      assigned={assignedIds.has(definition.id)}
                      selected={selectedId === definition.id}
                      loading={loading}
                      showType={true}
                      onSelect={() => setSelectedId(definition.id)}
                    />
                  ))}
                </Flex>
              ))
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button
            variant="entityDirectorySecondary"
            isDisabled={loading}
            onClick={close}
          >
            {t('general.cancel')}
          </Button>
          <Button
            variant="entityDirectoryPrimary"
            isDisabled={!canAssign}
            isLoading={loading}
            onClick={assign}
          >
            {t('suppliers.workspace.assign')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  function close() {
    if (!loading) onClose();
  }

  async function assign() {
    if (!canAssign) return;
    const response = await request(material.id, selectedId);
    if (response.hasError) return;

    toast({
      title: t('customerComplianceDocuments.assigned'),
      status: 'success',
      isClosable: true,
    });
    materialComplianceDocumentEvents.changed.dispatch(material.id);
    onClose();
  }
}
